import { Add, ArrowBack, Close, OpenInFull } from '@mui/icons-material'
import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { ConversationStatus, ConversationType } from 'models/conversations'
import { FC, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { GRAY_1000, GRAY_200, GRAY_300, GRAY_800, GRAY_900, PAPER_WHITE, WHITE } from 'constants/styling/theme'
import { useCreateConversation, useGetClosedConversationPages, useGetConversations, useSendMessage } from 'dataQueries/conversations.query'

import { AIDisclaimer } from '../AIDisclaimer/AIDisclaimer'
import { AIGreetingBubble } from './AIGreetingBubble'
import { CloseConversationMenu } from '../CloseConversationMenu'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import Box from '@mui/material/Box'
import { CHAT_WIDGET_BOTTOM_VAR, CHAT_WIDGET_RIGHT_VAR } from '../helpers/chatWidgetLayout'
import CircularProgress from '@mui/material/CircularProgress'
import { ConversationOverview } from './ConversationOverview/ConversationOverview'
import { ConversationPlaceholder } from './ConversationPlaceholder'
import { ConversationThread } from '../ConversationThread/ConversationThread'
import { StartConversationButton } from './StartConversationButton'
import IconButton from '@mui/material/IconButton'
import { MessageInput } from './MessageInput'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { SupportClosedNotice } from '../SupportHours/SupportClosedNotice'
import { SupportHoursSummary } from '../SupportHours/SupportHoursSummary'
import Typography from '@mui/material/Typography'
import { countReplyNeeded } from '../helpers/conversationOverview'
import { flattenConversationPages } from 'dataQueries/conversationListCache'
import { getThreadHeaderTitle } from '../helpers/conversationDisplay'
import { mergeForConversationList } from '../helpers/conversationListStatus'
import { logger } from 'utils/logging'
import { AttachmentUploadCancelledError, AttachmentUploadError, useChatAttachments } from '../helpers/useChatAttachments'
import axios from 'axios'
import { useIdempotentClientMessageId } from '../helpers/useIdempotentClientMessageId'
import { useChatWidget } from '../ChatWidget.context'
import { useCurrentPropertyContext } from '../helpers/useCurrentPropertyContext'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

const REPLY_NEEDED_SUMMARY_COLOR = '#B23A1E'

/**
 * Internal sub-view of the reduced chat popover. Drives the state machine that mirrors
 * the Figma "Layout and global navigation" frames:
 *
 *   placeholder  → user has no conversations yet (welcome state)
 *   list         → user has conversations (shown as a scrollable list)
 *   chatting     → greeting + composer for a new Ops support or assistant conversation
 *   thread       → user picked an existing conversation; shows its message history + composer
 *                  inside the popover (no drawer expansion)
 */
type View = 'placeholder' | 'list' | 'chatting' | 'thread'

type ComposerSource = 'list' | 'placeholder' | 'closed_thread'

/** Reduced floating popover for the chat widget (Figma "Layout and global navigation" frames). */
export const NewConversation: FC = () => {
  const { t } = useTranslation(['chat'])
  const { expand, close, setActiveConversation, selectedConversationId, isRestoring, isNewConversationRequested, consumeNewConversationRequest, currentUserId: participantId } = useChatWidget()
  const { baseUserData } = useUserData()
  const { spawnErrorToast } = useSnackbar()
  useCurrentPropertyContext() // route awareness reserved for future contextual greetings

  // Platform UUID exposed by /user/me as `internalId` (cf. UserProfileBaseDTO backend-side). Used to tell
  // the caller apart from the other participants when rendering a thread; the support conversation itself
  // needs no ids — the backend resolves the initiator from the authenticated principal.
  const currentUserId = baseUserData?.internalId
  const { data: ongoingPage } = useGetConversations(ConversationStatus.ACTIVE)
  const closedPages = useGetClosedConversationPages()
  const closedConversations = useMemo(() => flattenConversationPages(closedPages.data), [closedPages.data])
  const conversations = useMemo(
    () => mergeForConversationList(ongoingPage?.content ?? [], closedConversations),
    [ongoingPage, closedConversations]
  )
  const {
    hasNextPage: hasMoreClosed,
    isFetchingNextPage: isFetchingMoreClosed,
    isFetchNextPageError: failedToFetchMoreClosed,
    fetchNextPage: fetchMoreClosed,
  } = closedPages
  const hasConversations = conversations.length > 0
  const overviewUserId = participantId ?? currentUserId
  const replyNeededCount = useMemo(() => countReplyNeeded(conversations, overviewUserId), [conversations, overviewUserId])

  // Default sub-view derives from whether the user already has conversations. The user can then
  // navigate forward (start new) or backward (back arrow) without losing the data on which the
  // default was computed. Widget opening lands here (list when conversations exist, welcome
  // placeholder otherwise) — the user explicitly chose this over an auto-open-most-recent flow.
  const defaultView: View = hasConversations ? 'list' : 'placeholder'

  // The one exception is a thread the user left open, in this tab or another (QA-377): that is the
  // conversation they are having, not the most recent one, so reopening lands back inside it.
  const hasOpenThread = !!selectedConversationId &&
    conversations.some((conversation) => conversation.id === selectedConversationId && conversation.status !== ConversationStatus.CLOSED)
  const openingView: View = hasOpenThread ? 'thread' : defaultView
  const [view, setView] = useState<View>(openingView)

  // The conversation list query is in-flight on first popover mount (cache is cold), so the initial
  // `openingView` resolves to 'placeholder' even for users who do have conversations. Promote the
  // view once data lands — but only while the user hasn't navigated away from it, so an explicit
  // `setView('chatting')` (or similar) is never clobbered by a late-arriving query. A restored thread is
  // never demoted either: if it gets closed while on screen it stays, read-only, like an opened one.
  const hasNavigatedRef = useRef(false)
  useLayoutEffect(() => {
    if (hasNavigatedRef.current) return
    setView((current) => (current === 'thread' ? current : openingView))
  }, [openingView])
  const isAwaitingRestore = isRestoring && (view === 'list' || view === 'placeholder')
  const navigate = (next: View) => {
    hasNavigatedRef.current = true
    setView(next)
  }

  const createConversation = useCreateConversation()
  const sendMessage = useSendMessage()

  // Both flags must be on: ALLOW_EXTERNAL_COMMUNICATION_PF gates the widget itself (checked where it is
  // mounted), this one gates the assistant channel and mirrors the backend flag of the same key. Off ⇒ the
  // widget behaves exactly as before.
  const isAssistantEnabled = useFeatureFlag(FeatureFlag.ALLOW_AI_SUPPORT_ASSISTANT)

  // Which channel the composer will open on its first message. The AI channel is backed by no Case and costs
  // nothing to open; AGENT_CLIENT opens a Salesforce Case and a Slack channel, hence the care below.
  const [composerChannel, setComposerChannel] = useState<ConversationType>(ConversationType.AGENT_CLIENT)
  const attachments = useChatAttachments({ conversationType: composerChannel })
  const clientMessageIds = useIdempotentClientMessageId()
  const [isFirstMessageInFlight, setIsFirstMessageInFlight] = useState(false)
  const composerGenerationRef = useRef(0)
  const isAssistantComposer = composerChannel === ConversationType.AI_SUPPORT

  // The first question is posted here and then the view switches to the thread, so the "assistant is
  // answering" indicator has to be handed over — otherwise it would be missing on the very first turn.
  const [awaitingAssistantSince, setAwaitingAssistantSince] = useState<number | undefined>(undefined)

  // Creating a conversation opens a Salesforce Case and a Slack channel, so it must happen at most once
  // per composer session. Held outside `handleSubmitMessage` so a retry after a failed first-message
  // send reuses the conversation instead of minting a second Case.
  const pendingConversationIdRef = useRef<string | undefined>(undefined)

  const isChatLikeView = view === 'chatting' || view === 'thread'
  const showBackArrow = isChatLikeView

  const goBack = () => {
    // Leaving the thread view drops the active selection so the next time the user lands on the
    // list, the row isn't pre-highlighted from a stale selection.
    if (view === 'thread') setActiveConversation(null)
    navigate(defaultView)
  }
  // Support flow (ISSUE-2345): the widget's primary entry opens the Client/Creative↔Ops support composer.
  // With the assistant on (Phase 2) it opens the assistant composer instead.
  // Entering a composer clears the retry slot so a deliberate "new conversation" never posts into
  // the previous one.
  const startComposer = (channel: ConversationType, next: View) => {
    pendingConversationIdRef.current = undefined
    attachments.clear()
    clientMessageIds.release()
    composerGenerationRef.current += 1
    setIsFirstMessageInFlight(false)
    setAwaitingAssistantSince(undefined)
    setComposerChannel(channel)
    navigate(next)
  }

  const startSupportConversation = (source: ComposerSource) => {
    const channel = isAssistantEnabled ? ConversationType.AI_SUPPORT : ConversationType.AGENT_CLIENT
    logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_COMPOSER_OPENED, { conversationType: channel, source })
    startComposer(channel, 'chatting')
  }

  const startSupportConversationRef = useRef(startSupportConversation)
  startSupportConversationRef.current = startSupportConversation
  useLayoutEffect(() => {
    if (!isNewConversationRequested) return
    consumeNewConversationRequest()
    startSupportConversationRef.current('closed_thread')
  }, [isNewConversationRequested, consumeNewConversationRequest])

  // Drops the composer's handed-over wait: it belongs to the thread the composer navigated to, not to
  // whichever conversation the user opens next from the list.
  const openThread = (conversationId: string) => {
    setAwaitingAssistantSince(undefined)
    setActiveConversation(conversationId)
    navigate('thread')
  }

  // Creates the conversation for the picked channel and persists the first message. The backend resolves
  // the initiator from the authenticated principal — no counterparty is picked here. AGENT_CLIENT waits for
  // a BKBN Ops agent to join via Slack; AI_SUPPORT is answered by the assistant, and a new conversation started
  // here asks for a fresh thread (`startNew`), so it never lands back in the history of an earlier one.
  const handleSubmitMessage = async (text: string) => {
    if (createConversation.isPending || sendMessage.isPending || attachments.isUploading || isFirstMessageInFlight) return

    const isCurrentComposer = attachments.captureSession()
    const composerGeneration = composerGenerationRef.current
    setIsFirstMessageInFlight(true)
    const attachmentCount = attachments.items.length
    let failedStep: 'create' | 'upload' | 'send' = 'create'
    let attachmentIds: string[] = []
    try {
      if (!pendingConversationIdRef.current) {
        // The backend rejects `startNew` on any type but AI_SUPPORT, so it is only ever sent there.
        const response = await createConversation.mutateAsync({
          type: composerChannel,
          ...(isAssistantComposer ? { startNew: true } : {}),
        })
        if (!isCurrentComposer()) return
        pendingConversationIdRef.current = response.data.id
      }
      const conversationId = pendingConversationIdRef.current

      failedStep = 'upload'
      attachmentIds = await attachments.uploadAll(conversationId)

      failedStep = 'send'
      await sendMessage.mutateAsync({
        conversationId,
        content: text,
        clientMessageId: clientMessageIds.idFor(conversationId, text, attachmentIds),
        ...(attachmentIds.length > 0 && { attachmentIds }),
      })
      clientMessageIds.release()

      if (attachmentIds.length > 0) {
        logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_ATTACHMENT_SENT, {
          conversationType: composerChannel,
          count: attachmentIds.length,
          surface: 'new_conversation',
          fileOnly: !text,
        })
      }
      if (!isCurrentComposer()) return
      attachments.clear()

      // On the assistant channel the answer is being computed right now: carry the wait into the thread
      // so the indicator is already up when it mounts.
      if (isAssistantComposer) setAwaitingAssistantSince(Date.now())

      // Track the active conversation without expanding the drawer — the user explicitly expands via the
      // dedicated icon; sending a message keeps them in the compact widget. Switch to the thread view so
      // subsequent messages land in the same conversation history.
      setActiveConversation(conversationId)
      navigate('thread')
    } catch (error) {
      if (error instanceof AttachmentUploadCancelledError || !isCurrentComposer()) return
      spawnErrorToast(t('chat:errors.send_failed'))
      const uploadFailure = error instanceof AttachmentUploadError ? error.failure : undefined
      logger.error('ChatWidget: support conversation creation / first-message send failed', error, {
        context: {
          component: 'NewConversation',
          action: 'handleSubmitMessage',
          metadata: {
            channel: composerChannel,
            startNew: isAssistantComposer,
            failedStep,
            conversationId: pendingConversationIdRef.current,
            attachmentCount,
            ...(uploadFailure && { uploadStep: uploadFailure.step, failedAttachments: uploadFailure.failed, status: uploadFailure.status }),
            ...(failedStep === 'send' && { attachmentIds, status: axios.isAxiosError(error) ? error.response?.status : undefined }),
          },
        },
      })
      if (failedStep === 'send' && attachmentIds.length > 0) {
        logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_ATTACHMENT_UPLOAD_FAILED, {
          conversationType: composerChannel,
          step: 'send',
          count: attachmentIds.length,
        })
      }
      // Re-thrown so the composer restores the draft — the conversation is reused on retry, but the
      // message itself would otherwise be gone and the user would have to retype it.
      throw error
    } finally {
      if (composerGenerationRef.current === composerGeneration) setIsFirstMessageInFlight(false)
    }
  }

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === selectedConversationId),
    [conversations, selectedConversationId]
  )
  const isNewConversationView = view === 'chatting'
  const threadOthers = activeConversation?.participants.filter((p) => p.userId !== currentUserId) ?? []
  const threadParticipantsLabel = threadOthers.map((p) => p.name).filter(Boolean).join(', ')

  // A support conversation holds only its initiator until an Ops agent joins via Slack, so the participant
  // label would be empty (and later just "Agent"). Use the type-aware display title instead so the thread
  // reads as support from the first message on.
  const supportThreadTitle = activeConversation?.type === ConversationType.AGENT_CLIENT
    ? getThreadHeaderTitle(activeConversation, currentUserId, t)
    : undefined

  const showThreadSupportHours = view === 'thread' && (activeConversation?.type === ConversationType.AGENT_CLIENT ||
    activeConversation?.status === ConversationStatus.ESCALATED)
  const isSupportComposer = view === 'chatting' && composerChannel === ConversationType.AGENT_CLIENT

  const isListView = view === 'list'
  const headerButtonSx = {
    border: `1px solid ${isListView ? GRAY_300 : GRAY_200}`,
    backgroundColor: isListView ? WHITE : undefined,
    width: isListView ? '4.4rem' : '3.2rem',
    height: isListView ? '4.4rem' : '3.2rem',
  }

  const headerTitle = view === 'thread'
    ? (supportThreadTitle || threadParticipantsLabel || t('chat:thread.title_fallback'))
    : isNewConversationView
      ? t('chat:new_conversation.title')
      : t('chat:conversations_title')

  return (
    <Paper
      elevation={8}
      sx={{
        width: `min(40rem, calc(100vw - var(${CHAT_WIDGET_RIGHT_VAR}) - 1.6rem))`,
        height: `min(60rem, calc(100vh - 9.6rem - var(${CHAT_WIDGET_BOTTOM_VAR})))`,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '1.6rem',
        overflow: 'hidden',
        backgroundColor: WHITE,
      }}
    >

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          padding: '1.6rem 1.6rem 1.2rem',
          borderBottom: isListView ? 'none' : `1px solid ${GRAY_200}`,
          backgroundColor: isListView ? PAPER_WHITE : undefined,
          gap: '0.4rem',
        }}
      >

        <Stack direction="row" alignItems="center" gap="0.6rem" minWidth={0}>
          {showBackArrow &&
            <IconButton
              size="small"
              onClick={goBack}
              aria-label={t('chat:actions.back')}
              sx={{ border: `1px solid ${GRAY_200}`, width: '3.2rem', height: '3.2rem' }}
            >
              <ArrowBack sx={{ fontSize: '1.8rem', color: GRAY_800 }} />
            </IconButton>
          }

          <Stack minWidth={0}>

            <Typography variant="text-xl" fontWeight={600} color={GRAY_900} noWrap fontSize={isListView ? '2.2rem' : undefined}>
              {headerTitle}
            </Typography>

            {view === 'list' && replyNeededCount > 0 &&
              <Typography variant="text-sm" fontWeight={600} color={REPLY_NEEDED_SUMMARY_COLOR} noWrap fontSize="1.3rem">
                {t('chat:overview.need_reply_summary', { count: replyNeededCount })}
              </Typography>
            }

            {showThreadSupportHours &&
              <SupportHoursSummary />
            }

          </Stack>
        </Stack>

        <Stack direction="row" gap="0.6rem">

          {view === 'thread' && !!activeConversation &&
            <CloseConversationMenu
              conversationId={activeConversation.id}
              status={activeConversation.status}
              conversationType={activeConversation.type}
              triggerSx={{ border: `1px solid ${GRAY_200}`, width: '3.2rem', height: '3.2rem', color: GRAY_800 }}
              onClosed={goBack}
            />
          }

          <IconButton
            size="small"
            onClick={expand}
            aria-label={t('chat:actions.expand')}
            sx={headerButtonSx}
          >
            <OpenInFull sx={{ fontSize: isListView ? '1.8rem' : '1.6rem', color: GRAY_800 }} />
          </IconButton>

          <IconButton
            size="small"
            onClick={close}
            aria-label={t('chat:actions.close')}
            sx={headerButtonSx}
          >
            <Close sx={{ fontSize: '1.8rem', color: GRAY_800 }} />
          </IconButton>
        </Stack>

      </Stack>

      <Box flex={1} minHeight={0} overflow="auto" sx={{ display: 'flex', flexDirection: 'column' }}>

        {isAwaitingRestore &&
          <Stack flex={1} alignItems="center" justifyContent="center">
            <CircularProgress size={24} />
          </Stack>
        }

        {view === 'placeholder' && !isAwaitingRestore &&
          <ConversationPlaceholder />
        }

        {view === 'list' && !isAwaitingRestore &&
          <ConversationOverview
            conversations={conversations}
            currentUserId={overviewUserId}
            selectedConversationId={selectedConversationId}
            onOpen={openThread}
            hasMoreConversations={hasMoreClosed && !failedToFetchMoreClosed}
            isLoadingMoreConversations={isFetchingMoreClosed}
            isRefreshingConversations={closedPages.isFetching && !isFetchingMoreClosed}
            onLoadMoreConversations={() => void fetchMoreClosed()}
          />
        }

        {/* Static welcome copy — nothing has answered yet; replies arrive in the thread view. */}
        {view === 'chatting' &&
          <Stack gap="1.2rem" padding="1.6rem">
            <AIGreetingBubble
              sender={isAssistantComposer ? 'assistant' : 'agent'}
              message={isAssistantComposer ? t('chat:greeting.assistant_intro', { defaultValue: t('chat:greeting.assistant') }) : t('chat:greeting.generic')}
            />
          </Stack>
        }

        {view === 'thread' && selectedConversationId &&
          <ConversationThread
            conversationId={selectedConversationId}
            conversationType={activeConversation?.type}
            awaitingAssistantSince={awaitingAssistantSince}
            currentUserId={currentUserId}
            compact
            readOnly={activeConversation?.status === ConversationStatus.CLOSED}
            conversationStatus={activeConversation?.status}
            conversationLanguage={activeConversation?.language}
            reopenedAt={activeConversation?.reopenedAt}
            participants={activeConversation?.participants}
          />
        }

      </Box>

      {(view === 'placeholder' || view === 'list') && !isAwaitingRestore &&
        <Box sx={{ padding: '1.2rem 1.6rem 1.6rem', borderTop: `1px solid ${GRAY_300}`, backgroundColor: PAPER_WHITE }}>
          <StartConversationButton
            label={t('chat:new_conversation.start')}
            startIcon={<Add />}
            onClick={() => startSupportConversation(view === 'placeholder' ? 'placeholder' : 'list')}
            sx={{
              height: '4.8rem',
              padding: '0 1.2rem',
              backgroundColor: GRAY_1000,
              fontWeight: 600,
              '& .MuiButton-startIcon svg': { fontSize: '1.8rem' },
              '&:hover': { backgroundColor: GRAY_1000 },
            }}
          />
        </Box>
      }

      {/*
        The disclaimer belongs to the AI channel only: on the AGENT_CLIENT composer a human Ops agent answers,
        so warning about AI answers would mislabel the conversation.
      */}
      {view === 'chatting' && isAssistantComposer &&
        <AIDisclaimer />
      }

      {isSupportComposer &&
        <SupportHoursSummary centered sx={{ padding: '0.8rem 1.6rem 0' }} />
      }

      {isSupportComposer &&
        <SupportClosedNotice surface="composer" />
      }

      {view === 'chatting' &&
        <MessageInput
          placeholder={t('chat:input.placeholder')}
          submitting={createConversation.isPending || sendMessage.isPending || isFirstMessageInFlight}
          autoFocus
          onSubmit={handleSubmitMessage}
          onAttachFromComputer={attachments.addFiles}
          attachDisabled={isFirstMessageInFlight}
          attachments={attachments}
          sendAriaLabel={t('chat:actions.send')}
          attachAriaLabel={t('chat:actions.attach')}
        />
      }

    </Paper>
  )
}
